<!-- eslint-disable vue/multi-word-component-names -->
<template>
  <ListPageShell>
    <template #header>
      <div class="list-page-panel is-header-minimal">
        <div class="list-page-heading">
          <div class="list-page-heading-main">
            <h1 class="list-page-title">素材类型管理</h1>
            <p class="list-page-description">统一管理素材分类与资料类型，支持查询、添加、编辑和删除</p>
          </div>
        </div>
      </div>
    </template>
    <div class="flex h-full bg-slate-50">
    <!-- 左侧分类树 -->
    <div class="w-72 bg-white border-r border-slate-200 flex flex-col">
      <!-- 树头部 -->
      <div class="p-4 border-b border-slate-200">
        <div class="flex items-center justify-between mb-3">
          <h2 class="text-base font-semibold text-slate-800">资料分类</h2>
          <button
            @click="openCategoryModal()"
            class="p-1.5 text-primary-600 hover:bg-primary-50 rounded-lg transition-colors cursor-pointer"
            title="新增分类"
          >
            <svg class="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
            </svg>
          </button>
        </div>
      </div>

      <!-- 分类树内容 -->
      <div class="flex-1 overflow-y-auto p-2">
        <div v-if="loadingCategories" class="flex items-center justify-center py-8">
          <div class="w-6 h-6 border-2 border-primary-600 border-t-transparent rounded-full animate-spin"></div>
        </div>
        <div v-else-if="categoryTree.length === 0" class="text-center py-8 text-slate-400 text-sm">
          暂无分类数据
        </div>
        <div v-else>
          <CategoryTreeNode
            v-for="node in categoryTree"
            :key="node.id"
            :node="node"
            :selected-id="selectedCategoryId"
            @select="handleCategorySelect"
            @edit="openCategoryModal(node)"
            @delete="handleDeleteCategory(node)"
          />
        </div>
      </div>
    </div>

    <!-- 右侧类型列表 -->
    <div class="flex-1 flex flex-col overflow-hidden">
      <!-- 数据表格 -->
      <div class="list-page-table-panel">
        <PageFilterBar
          shell-class="filter-bar-shell is-embedded"
          body-class="filter-bar-body is-panel"
          :show-refresh="true"
          :refresh-loading="loadingDefinitions"
          @refresh="loadDefinitions"
        >
          <div class="filter-bar-field is-wide relative">
            <input
              v-model="searchKeyword"
              type="text"
              placeholder="搜索名称/编码..."
              class="form-search-input form-control-sm w-full"
              @keyup.enter="handleSearch"
            >
            <svg class="absolute left-3 top-2.5 h-4 w-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z"/>
            </svg>
          </div>

          <el-select
            v-model="searchStatus"
            placeholder="请选择状态"
            class="form-select form-control-sm filter-bar-field"
          >
            <el-option label="全部状态" value="" />
            <el-option label="启用" :value="1" />
            <el-option label="禁用" :value="0" />
          </el-select>

          <template #actions>
            <button
              v-permission="'material:definition:add'"
              :disabled="!selectedCategoryId"
              @click="openDefinitionModal()"
              class="btn-primary inline-flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 4v16m8-8H4"/>
              </svg>
              新增类型
            </button>
          </template>

          <template #searchActions>
            <button type="button" @click="handleSearch" class="btn-primary">搜索</button>
            <button type="button" @click="resetSearch" class="btn-secondary">重置</button>
          </template>

          <template #extra>
            <div v-if="selectedCategoryId && selectedCategoryName" class="flex items-center gap-2 text-sm text-slate-600">
              <svg class="w-4 h-4 text-slate-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"/>
              </svg>
              <span>当前选中了分类：</span>
              <span class="font-medium text-primary-700">{{ selectedCategoryName }}</span>
            </div>
          </template>
        </PageFilterBar>
        <div class="data-table-shell">
          <div class="data-table-scroll">
            <table class="data-table text-sm">
              <thead class="data-table-head">
                <tr>
                  <th class="data-table-th text-left">编码</th>
                  <th class="data-table-th text-left">名称</th>
                  <th class="data-table-th text-left">别名</th>
                  <th class="data-table-th text-center">允许多个</th>
                  <th class="data-table-th text-center w-20">排序</th>
                  <th class="data-table-th text-center w-24">状态</th>
                  <th class="data-table-th text-center w-32">操作</th>
                </tr>
              </thead>
              <tbody>
                <tr v-if="loadingDefinitions" class="data-table-row">
                  <td colspan="7" class="data-table-empty">
                    <div class="flex items-center justify-center gap-2 text-slate-500">
                      <div class="w-5 h-5 border-2 border-primary-600 border-t-transparent rounded-full animate-spin"></div>
                      加载中...
                    </div>
                  </td>
                </tr>
                <tr v-else-if="definitions.length === 0" class="data-table-row">
                  <td colspan="7" class="data-table-empty">
                    暂无数据{{ selectedCategoryId ? '，请选择其他分类或新增类型' : '，请先选择分类' }}
                  </td>
                </tr>
                <tr v-else v-for="def in definitions" :key="def.id" class="data-table-row">
                  <td class="data-table-td font-mono text-slate-600">{{ def.code }}</td>
                  <td class="data-table-td font-medium text-slate-900">{{ def.name }}</td>
                  <td class="data-table-td text-slate-500">{{ def.nameAlias || '-' }}</td>
                  <td class="data-table-td is-center">
                    <span
                      class="inline-flex items-center justify-center w-6 h-6 rounded-full text-xs font-medium"
                      :class="def.allowMultiple ? 'bg-green-100 text-green-700' : 'bg-slate-100 text-slate-500'"
                    >
                      {{ def.allowMultiple ? '是' : '否' }}
                    </span>
                  </td>
                  <td class="data-table-td is-center text-slate-900">{{ def.sort }}</td>
                  <td class="data-table-td is-center">
                    <span
                      class="status-badge"
                      :class="def.status === 1 ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'"
                    >
                      <span class="w-1.5 h-1.5 bg-current rounded-full mr-1"></span>
                      {{ def.status === 1 ? '启用' : '禁用' }}
                    </span>
                  </td>
                  <td class="data-table-td is-center">
                    <div class="flex items-center justify-center gap-1">
                      <button
                        @click="openDefinitionModal(def)"
                        class="table-action-button is-primary"
                        title="编辑" aria-label="编辑"
                      >
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z"/>
                        </svg>
                      </button>
                      <button
                        @click="handleDeleteDefinition(def)"
                        class="table-action-button is-danger"
                        title="删除" aria-label="删除"
                      >
                        <svg class="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"/>
                        </svg>
                      </button>
                    </div>
                  </td>
                </tr>
              </tbody>
            </table>
          </div>
        </div>

        <!-- 分页 -->
        <AppPagination
          v-if="total > 0"
          class="pagination-bridge"
          :current="page"
          :size="size"
          :total="total"
          :pages="totalPages"
          :page-sizes="[10, 20, 50]"
          @page-change="handlePageChange"
          @size-change="handlePageSizeChange"
        />
      </div>
    </div>

    <!-- 分类模态框 -->
    <CategoryFormModal
      v-if="showCategoryModal"
      :category="editingCategory"
      :categories="categoryList"
      @close="closeCategoryModal"
      @save="saveCategory"
    />

    <!-- 类型模态框 -->
    <DefinitionFormModal
      v-if="showDefinitionModal"
      :definition="editingDefinition"
      :category-id="selectedCategoryId"
      @close="closeDefinitionModal"
      @save="saveDefinition"
    />

    <!-- 删除确认 -->
    <DeleteConfirmModal
      v-if="showDeleteModal"
      :title="deleteConfig.title"
      :message="deleteConfig.message"
      @close="closeDeleteModal"
      @confirm="confirmDelete"
    />
    </div>
  </ListPageShell>
</template>

<script setup>
import { ref, reactive, computed, onMounted } from 'vue'
import { ElMessage } from 'element-plus'
import {
  getMaterialCategories,
  getMaterialDefinitions,
  createMaterialCategory,
  updateMaterialCategory,
  deleteMaterialCategory,
  createMaterialDefinition,
  updateMaterialDefinition,
  deleteMaterialDefinition
} from '@/api/customer'
import CategoryTreeNode from './components/CategoryTreeNode.vue'
import AppPagination from '@/components/AppPagination.vue'
import ListPageShell from '@/components/ListPageShell.vue'
import PageFilterBar from '@/components/PageFilterBar.vue'
import CategoryFormModal from './components/CategoryFormModal.vue'
import DefinitionFormModal from './components/DefinitionFormModal.vue'
import DeleteConfirmModal from '@/components/DeleteConfirmModal.vue'

// 分类相关
const loadingCategories = ref(false)
const categoryTree = ref([])
const categoryList = ref([])
const selectedCategoryId = ref(null)
const selectedCategoryName = ref('')

// 类型相关
const loadingDefinitions = ref(false)
const definitions = ref([])
const total = ref(0)
const page = ref(1)
const size = ref(10)
const searchKeyword = ref('')
const searchStatus = ref('')

// 模态框状态
const showCategoryModal = ref(false)
const showDefinitionModal = ref(false)
const showDeleteModal = ref(false)
const editingCategory = ref(null)
const editingDefinition = ref(null)

// 删除配置
const deleteConfig = reactive({
  type: '',
  id: null,
  title: '',
  message: ''
})

// 计算属性
const totalPages = computed(() => {
  if (!total.value || !size.value) return 1
  return Math.max(1, Math.ceil(total.value / size.value))
})

// 加载分类树
async function loadCategories() {
  loadingCategories.value = true
  try {
    const data = await getMaterialCategories()
    categoryTree.value = buildTree(data || [])
    categoryList.value = data || []
  } catch (error) {
    console.error('加载分类失败:', error)
    ElMessage.error(error.message || '加载分类失败')
  } finally {
    loadingCategories.value = false
  }
}

// 构建树形结构
function buildTree(list, parentId = null) {
  return list
    .filter(item => parentId === null ? item.parentId === 0 || item.parentId === null : item.parentId === parentId)
    .sort((a, b) => (a.sort || 0) - (b.sort || 0))
    .map(item => ({
      ...item,
      children: buildTree(list, item.id)
    }))
}

// 加载类型列表
async function loadDefinitions() {
  console.log('loadDefinitions 被调用, selectedCategoryId:', selectedCategoryId.value)
  if (!selectedCategoryId.value) {
    definitions.value = []
    total.value = 0
    return
  }

  loadingDefinitions.value = true
  try {
    const params = {
      page: page.value,
      size: size.value,
      categoryId: selectedCategoryId.value,
      keyword: searchKeyword.value || undefined,
      status: searchStatus.value || undefined
    }
    const data = await getMaterialDefinitions(params)
    definitions.value = data?.records || []
    total.value = Number(data?.total || 0)
  } catch (error) {
    console.error('加载类型列表失败:', error)
    ElMessage.error(error.message || '加载类型列表失败')
  } finally {
    loadingDefinitions.value = false
  }
}

// 选择分类
function handleCategorySelect(category) {
  console.log('分类被选中:', category)
  selectedCategoryId.value = category.id
  selectedCategoryName.value = category.name
  page.value = 1
  loadDefinitions()
}

// 搜索
function handleSearch() {
  page.value = 1
  loadDefinitions()
}

// 重置搜索
function resetSearch() {
  searchKeyword.value = ''
  searchStatus.value = ''
  page.value = 1
  loadDefinitions()
}

// 分页
function handlePageChange(p) {
  page.value = p
  loadDefinitions()
}

function handlePageSizeChange(s) {
  size.value = s
  page.value = 1
  loadDefinitions()
}

// 分类操作
function openCategoryModal(category = null) {
  editingCategory.value = category
  showCategoryModal.value = true
}

function closeCategoryModal() {
  showCategoryModal.value = false
  editingCategory.value = null
}

async function saveCategory(data) {
  try {
    if (editingCategory.value?.id) {
      await updateMaterialCategory(editingCategory.value.id, data)
      ElMessage.success('修改成功')
    } else {
      await createMaterialCategory(data)
      ElMessage.success('创建成功')
    }
    await loadCategories()
    closeCategoryModal()
  } catch (error) {
    ElMessage.error(error.message || '操作失败')
  }
}

function handleDeleteCategory(category) {
  deleteConfig.type = 'category'
  deleteConfig.id = category.id
  deleteConfig.title = '删除分类'
  deleteConfig.message = `确定要删除分类"${category.name}"吗？删除后该分类下的资料类型将无法正常显示。`
  showDeleteModal.value = true
}

// 类型操作
function openDefinitionModal(definition = null) {
  editingDefinition.value = definition
  showDefinitionModal.value = true
}

function closeDefinitionModal() {
  showDefinitionModal.value = false
  editingDefinition.value = null
}

async function saveDefinition(data) {
  try {
    if (editingDefinition.value?.id) {
      await updateMaterialDefinition(editingDefinition.value.id, data)
      ElMessage.success('修改成功')
    } else {
      await createMaterialDefinition(data)
      ElMessage.success('创建成功')
    }
    await loadDefinitions()
    closeDefinitionModal()
  } catch (error) {
    ElMessage.error(error.message || '操作失败')
  }
}

function handleDeleteDefinition(definition) {
  deleteConfig.type = 'definition'
  deleteConfig.id = definition.id
  deleteConfig.title = '删除资料类型'
  deleteConfig.message = `确定要删除资料类型"${definition.name}"吗？此操作不可恢复。`
  showDeleteModal.value = true
}

// 删除确认
function closeDeleteModal() {
  showDeleteModal.value = false
}

async function confirmDelete() {
  try {
    if (deleteConfig.type === 'category') {
      await deleteMaterialCategory(deleteConfig.id)
      ElMessage.success('删除成功')
      if (selectedCategoryId.value === deleteConfig.id) {
        selectedCategoryId.value = null
        selectedCategoryName.value = ''
        definitions.value = []
      }
      await loadCategories()
    } else if (deleteConfig.type === 'definition') {
      await deleteMaterialDefinition(deleteConfig.id)
      ElMessage.success('删除成功')
      await loadDefinitions()
    }
  } catch (error) {
    ElMessage.error(error.message || '删除失败')
  }
  closeDeleteModal()
}

// 初始化
onMounted(() => {
  loadCategories()
})
</script>

<style scoped>
/* 滚动条样式 */
::-webkit-scrollbar {
  width: 6px;
  height: 6px;
}

::-webkit-scrollbar-track {
  background: transparent;
}

::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 3px;
}

::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}
</style>
